import React from "react";
import { Clock, Lock } from "lucide-react";
import { useStudent } from "../../context/StudentContext";

interface PremiumGateProps {
  isLocked: boolean;
  children: React.ReactNode;
  buttonLabel?: string;
  className?: string;
  semester?: number | null;
  hasPendingRequest?: boolean;
}

export function PremiumGate({
  isLocked,
  children,
  buttonLabel,
  className = "",
  semester,
  hasPendingRequest: propHasPending,
}: PremiumGateProps) {
  const { openUpgrade, hasPendingRequest: contextHasPending } = useStudent();

  if (!isLocked) {
    return <>{children}</>;
  }

  const isPending = propHasPending ?? (semester ? contextHasPending(semester) : false);
  const defaultLabel = semester ? `Unlock Semester ${semester}` : "Get premium";
  const label = isPending ? "Awaiting review" : (buttonLabel || defaultLabel);

  return (
    <div className={`relative ${className}`}>
      {/* Dimmed, non-interactive content, removed from tab order */}
      <div
        className="opacity-40 pointer-events-none select-none transition-opacity"
        aria-hidden="true"
        tabIndex={-1}
      >
        {children}
      </div>

      {/* Centered overlay with unlock or awaiting review action */}
      <div className="absolute inset-0 flex items-center justify-center p-3 bg-background/50 backdrop-blur-[0.5px] rounded-xs">
        {isPending ? (
          <span
            aria-label="Payment awaiting review"
            className="px-3.5 py-1.5 text-xs font-semibold rounded-xs inline-flex items-center gap-1.5 bg-muted text-muted-foreground border border-border cursor-default shadow-xs"
          >
            <Clock size={13} />
            {label}
          </span>
        ) : (
          <button
            type="button"
            onClick={() => openUpgrade({ semester })}
            aria-label={label}
            className="btn btn-primary px-3.5 py-1.5 text-xs font-semibold rounded-xs inline-flex items-center gap-1.5 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-1"
          >
            <Lock size={13} />
            {label}
          </button>
        )}
      </div>
    </div>
  );
}
